// src/homeComponents/sections/ExploreMotors.jsx

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CarFront,
  ArrowRight,
  Gauge,
  Fuel,
  CalendarDays,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Inbox,
} from "lucide-react"; 
import vehicleSellService from "../services/vehicleSellService";

const inr = (n) => Number(n || 0).toLocaleString("en-IN");

/* ============================================================
   SINGLE CARD
   ============================================================ */
const VehicleMiniCard = ({ vehicle }) => {
  const cover = (vehicle.images || [])[0];

  return (
    <Link
      to="/motors/selling"
      className="
        group flex w-[260px] shrink-0 flex-col overflow-hidden
        rounded-2xl border border-[#E2E8E5] bg-white
        transition-all duration-300
        hover:-translate-y-1
        hover:border-[#00A260]/40
        hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)]
        sm:w-[280px]
      "
    >
      {/* Image */}
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#F1F5F3]">
        {cover ? (
          <img
            src={cover}
            alt={`${vehicle.vehicle_brand} ${vehicle.vehicle_model}`}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-[#94A3B8]">
            <CarFront className="h-8 w-8" />
          </div>
        )}

        {/* Type badge */}
        <div className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-0.5 text-[10px] font-bold uppercase text-[#061A3A] backdrop-blur">
          {vehicle.vehicle_type || "Vehicle"}
        </div>

        {/* Negotiable badge */}
        {vehicle.negotiable ? (
          <div className="absolute right-3 top-3 rounded-full bg-[#00A260] px-2 py-0.5 text-[9px] font-bold uppercase text-white">
            Negotiable
          </div>
        ) : null}
      </div>

      {/* Info */}
      <div className="flex flex-1 flex-col p-4">
        <h3 className="truncate text-sm font-bold text-[#061A3A]">
          {vehicle.vehicle_brand} {vehicle.vehicle_model}
        </h3>

        <p className="mt-0.5 text-[11px] text-[#64748B]">
          {[
            vehicle.vehicle_variant,
            vehicle.manufacturing_year,
            vehicle.fuel_type,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>

        {/* Specs row */}
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-[#64748B]">
          {vehicle.km_driven ? (
            <span className="flex items-center gap-1">
              <Gauge className="h-3 w-3 text-[#00A260]" />
              {inr(vehicle.km_driven)} km
            </span>
          ) : null}

          {vehicle.fuel_type ? (
            <span className="flex items-center gap-1">
              <Fuel className="h-3 w-3 text-[#00A260]" />
              {vehicle.fuel_type}
            </span>
          ) : null}

          {vehicle.manufacturing_year ? (
            <span className="flex items-center gap-1">
              <CalendarDays className="h-3 w-3 text-[#00A260]" />
              {vehicle.manufacturing_year}
            </span>
          ) : null}
        </div>

        {/* Price + location */}
        <div className="mt-auto flex items-end justify-between pt-4">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-[#94A3B8]">
              Price
            </p>
            <p className="text-base font-extrabold text-[#00A260]">
              ₹{inr(vehicle.expected_price)}
            </p>
          </div>

          {vehicle.city ? (
            <p className="flex items-center gap-1 text-[10px] text-[#94A3B8]">
              <MapPin className="h-3 w-3" />
              {vehicle.city}
            </p>
          ) : null}
        </div>
      </div>
    </Link>
  );
};

/* ============================================================
   SKELETON
   ============================================================ */
const SkeletonCard = () => (
  <div className="w-[260px] shrink-0 animate-pulse overflow-hidden rounded-2xl border border-[#E2E8E5] bg-white sm:w-[280px]">
    <div className="aspect-[16/10] bg-[#F1F5F3]" />
    <div className="space-y-2 p-4">
      <div className="h-3 w-3/4 rounded bg-[#F1F5F3]" />
      <div className="h-3 w-1/2 rounded bg-[#F1F5F3]" />
      <div className="h-4 w-1/3 rounded bg-[#F1F5F3]" />
    </div>
  </div>
);

/* ============================================================
   MAIN SECTION
   ============================================================ */
const ExploreMotors = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = React.useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await vehicleSellService.listPublishedVehicles({
          limit: 6,
        });
        setVehicles(data || []);
      } catch (err) {
        console.error("Explore motors load error:", err);
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = 300;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section className="bg-[#F8FAF9] py-14 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* ================= HEADER ================= */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-xl">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#061A3A] text-[#8BF108]">
                <CarFront className="h-4.5 w-4.5" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00A260] sm:text-xs">
                NJV Motors
              </span>
            </div>

            <h2 className="mt-3 text-2xl font-extrabold leading-tight tracking-tight text-[#061A3A] sm:text-3xl lg:text-4xl">
              Explore Vehicles for Sale
            </h2>

            <p className="mt-3 text-sm leading-6 text-[#64748B]">
              Hand-picked, verified vehicles listed by owners. Direct contact,
              no middlemen.
            </p>
          </div>

          {/* Actions */}
          <div className="flex shrink-0 items-center gap-2">
            {/* Scroll buttons (desktop only) */}
            <div className="hidden items-center gap-2 sm:flex">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                aria-label="Scroll left"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E2E8E5] bg-white text-[#061A3A] transition hover:border-[#00A260] hover:text-[#00A260]"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                aria-label="Scroll right"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E2E8E5] bg-white text-[#061A3A] transition hover:border-[#00A260] hover:text-[#00A260]"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Explore CTA */}
            <Link
              to="/motors/selling"
              className="
                group inline-flex h-11 shrink-0 items-center gap-2
                rounded-xl bg-[#00A260] px-5 text-sm font-bold text-white
                shadow-md shadow-[#00A260]/15
                transition-all duration-200
                hover:-translate-y-0.5
                hover:bg-[#005543]
              "
            >
              Explore NJV Motors
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>

        {/* ================= HORIZONTAL ROW ================= */}
        {loading ? (
          <div className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-3 sm:mx-0 sm:px-0">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E2E8E5] bg-white p-10 text-center">
            <Inbox className="mx-auto h-8 w-8 text-[#CBD5D1]" />
            <p className="mt-3 text-sm font-semibold text-[#64748B]">
              No vehicles listed yet
            </p>
            <p className="mt-1 text-xs text-[#94A3B8]">
              Be the first to list your vehicle on NJV Motors.
            </p>
            {/* <Link
              to="/motors/sell"
              className="mt-4 inline-flex h-10 items-center gap-2 rounded-xl bg-[#00A260] px-4 text-xs font-bold text-white hover:bg-[#005543]"
            >
              Sell Your Vehicle
            </Link> */}
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="
              -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-3
              scroll-smooth
              [scrollbar-width:none]
              [&::-webkit-scrollbar]:hidden
              sm:mx-0 sm:px-0
            "
          >
            {vehicles.map((v) => (
              <div key={v.id} className="snap-start">
                <VehicleMiniCard vehicle={v} />
              </div>
            ))}
          </div>
        )}

        {/* ================= MOBILE SEE ALL ================= */}
        {!loading && vehicles.length > 0 ? (
          <div className="mt-6 flex justify-center sm:hidden">
            <Link
              to="/motors/selling"
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-[#00A260] bg-white px-6 text-sm font-bold text-[#00A260] transition hover:bg-[#F3FBF7]"
            >
              See all vehicles
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ExploreMotors;